import {
	Breakpoint,
	Button,
	Checkbox,
	Col,
	DatePicker,
	Modal,
	Pagination,
	Row,
	Select,
	Space,
	Spin,
	Table,
	Tooltip,
	Typography,
	Upload,
} from "antd";
import { theme } from "../../theme";
import CustomModalFooter from "../../Containers/CustomModalFooter";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { useHistory, useLocation } from "react-router-dom";
import {
	getUserId,
	useGetCompanyId,
	useIsAdmin,
	useIsCompany,
	useIsFacultyAdmin,
} from "../../utils/utilFunctions";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import {
	addNewDocument,
	deleteDocument,
	downloadDocument,
	getCompanyDocuments,
	rejectDocument,
	updateDocumentExpiration,
	updateSkipOfferValidationStatus,
} from "../../Requests/company-document-requests";
import {
	getDocuments,
	getFaculties,
	getProfessor,
} from "../../utils/reactQueriesConstants";
import {
	NOTIFICATION_TYPES,
	openNotification,
} from "../Notifications/NotificationsUtils";
import { StatusCodes } from "http-status-codes";
import { getProfessorById } from "../../Requests/academic-user-requests";
import dayjs from "../../utils/dayjs-setup";
import styles from "./DocumentsTable.module.scss";
import { downloadFile } from "../../utils/downloadUtils";
import ConfirmationModal from "../../Containers/ConfirmationModal";
import {
	CompanyDocumentDTO,
	CompanyDocumentStatusEnum,
	DocumentStatusEnum,
	DocumentTypeEnum,
	ErrorCodes,
	FacultyDTO,
} from "../../Api";
import { getAllFaculties } from "../../Requests/faculty-requests";
import CustomEditor from "../../CustomComponents/CustomEditor";
import { getErrorFromResponse } from "../../utils/responseUtils";
import useQueryFilters from "../../Hooks/useQueryFilters";
import { ArrowDownOutlined } from "@ant-design/icons";
import CustomButton from "../../CustomComponents/CustomButton";
import GenerateUniversityAccordsModal from "../UniversityAccords/GenerateUniversityAccordsModal";

const { Option } = Select;

const DocumentsTable = () => {
	const { t, i18n } = useTranslation();
	const history = useHistory();
	const location = useLocation<
		| {
				filters: any;
				oldSearchTerm: string;
				sortField: any;
				sortDirection: any;
				currentPage: any;
				pageSize: any;
				activeTab: string;
				company: string;
				companyId: string;
		  }
		| null
		| undefined
	>();
	const [searchTerm, setSearchTerm] = useState("");
	const [pageSize, setPageSize] = useState(10);
	const [currentPage, setCurrent] = useState(1);
	const [documentTypes, setDocumentTypes] = useState([]);
	const [selectedDocumentId, setSelectedDocumentId] = useState<any>(undefined);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [modalVisibility, setModalVisibility] = useState(false);
	const [facultyOptions, setFacultyOptions] = useState<any>([]);
	const [allFacultyOptions, setAllFacultyOptions] = useState<any>([]);
	const [facultySearch, setFacultySearch] = useState("");
	const [selectedFaculty, setSelectedFaculty] = useState(undefined);
	const [spinning, setSpinning] = useState(false);
	const [adminFaculties, setAdminFaculties] = useState<any>([]);
	const [file, setFile] = useState<Blob>();
	const [fileList, setFileList] = useState<any>([]);
	const [documentType, setDocumentType] = useState<any>(null);
	const [documentsOptions, setDocumentsOptions] = useState<any>([]);
	const [validity, setValidity] = useState<DocumentStatusEnum>(
		DocumentStatusEnum.All
	);
	const [expiresAt, setExpiresAt] = useState<any>(null);
	const [updateDoc, setUpdateDoc] = useState<any | null>(null);
	const [newDate, setNewDate] = useState<any>(null);
	const [filters, setFilters] = useState(null);
	const isFacultyAdmin = useIsFacultyAdmin();
	const isAdmin = useIsAdmin();
	const isCompany = useIsCompany();
	const queryClient = useQueryClient();
	const companyID = useGetCompanyId();
	const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
	const [rejectionMessage, setRejectionMessage] = useState("");
	const query = useQueryFilters({});

	const returnAction = t("universityAccord.uploadedFile");

	let locale = {
		emptyText: t("tableText.noDocuments"),
	};

	const handleShow = () => {
		setIsModalVisible(true);
	};

	const uploadDocument = (
		companyId: string,
		file: Blob,
		facultyId?: string,
		documentId?: string,
		expiresAt?: Date,
		documentType?: DocumentTypeEnum
	) => {
		addNewDocument(
			companyId,
			file,
			facultyId,
			documentId,
			documentType,
			expiresAt
		)
			.then(async () => {
				await queryClient.invalidateQueries(getDocuments);
				setDocumentType(null);
				setSelectedFaculty(undefined);
				setSpinning(false);
				setFile(undefined);
				setFileList([]);
				setSelectedDocumentId(undefined);
				setIsModalVisible(false);
				openNotification(
					t("documents.uploadedDocument"),
					t("documents.uploadSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch(() => {
				openNotification(
					t("usersText.errorTexts.error"),
					t("documents.uploadFail"),
					NOTIFICATION_TYPES.ERROR
				);
				setSpinning(false);
			});
	};

	const handleOk = () => {
		setSpinning(true);
		if (!isCompany) {
			if (location.state?.companyId && file !== undefined) {
				const isProtocol =
					documentType === DocumentTypeEnum.PracticeProtocol ||
					documentType === "practiceProtocol";
				uploadDocument(
					location.state?.companyId,
					file,
					selectedFaculty,
					selectedDocumentId,
					isProtocol && expiresAt
						? expiresAt?.toDate?.() ?? expiresAt
						: undefined,
					isProtocol
						? DocumentTypeEnum.PracticeProtocol
						: documentType === DocumentTypeEnum.CollaborationAccord
						? DocumentTypeEnum.CollaborationAccord
						: undefined
				);
			}
		} else {
			if (file !== undefined)
				uploadDocument(companyID, file, selectedFaculty, selectedDocumentId);
		}
	};

	const handleCancel = () => {
		setDocumentType(null);
		setSelectedFaculty(undefined);
		setFile(undefined);
		setSelectedDocumentId(undefined);
		setIsModalVisible(false);
		setIsRejectModalVisible(false);
	};

	const downloadFail = (_err: any) => {
		openNotification(
			t("universityAccord.error"),
			t("universityAccord.downloadError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const deleteCompanyDocument = (documentId: string) => {
		deleteDocument(documentId)
			.then(async () => {
				await queryClient.invalidateQueries(getDocuments);
				setModalVisibility(false);
				setDocumentType(null);
				setSelectedFaculty(undefined);
				setFile(undefined);
				setFileList([]);
				setSelectedDocumentId(undefined);
				openNotification(
					t("documents.deleteDocument"),
					t("documents.deleteSuccessfully"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch(() => {
				openNotification(
					t("usersText.errorTexts.error"),
					t("documents.deleteError"),
					NOTIFICATION_TYPES.ERROR
				);
			});
	};

	const fetchDataErrorNotification = (ex: any) => {
		if (ex.status && ex.status === StatusCodes.NOT_FOUND) {
			openNotification(
				t("account.error"),
				t("account.fetchUserDataError"),
				NOTIFICATION_TYPES.ERROR
			);
		} else {
			openNotification(
				t("account.error"),
				t("account.unknownError"),
				NOTIFICATION_TYPES.ERROR
			);
		}
	};

	const { data: adminData } = useQuery(
		[getProfessor, isAdmin, isFacultyAdmin],
		async () =>
			isAdmin || isFacultyAdmin ? getProfessorById(getUserId()) : null,
		{
			onError: fetchDataErrorNotification,
		}
	);

	const onCheckboxChange = (event: any, id: string) => {
		updateSkipOfferValidationStatus(id)
			.then(async () => {
				await queryClient.invalidateQueries(getDocuments);
				openNotification(
					t("documents.changeStatus"),
					t("documents.statusChangedSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);
			})
			.catch(() => {
				openNotification(
					t("usersText.errorTexts.error"),
					t("documents.statusChangedFailed"),
					NOTIFICATION_TYPES.ERROR
				);
			});
	};

	const rejectCompanyDocument = (documentId: string, message: string) => {
		setSpinning(true);

		rejectDocument(documentId, message)
			.then(() => {
				openNotification(
					t("documents.documentRejection"),
					t("documents.documentRejectionSuccess"),
					NOTIFICATION_TYPES.SUCCESS
				);

				setRejectionMessage("");
				setIsRejectModalVisible(false);
			})
			.catch(async (error) => {
				const errorMessage = await getErrorFromResponse(error);

				let notificationMessage;

				switch (errorMessage?.code) {
					case ErrorCodes.DocumentNotFound:
						notificationMessage = t("documents.documentNotFound");
						break;
					case ErrorCodes.Forbidden:
						notificationMessage = t("documents.notAllowed");
						break;
					default:
						notificationMessage = t("documents.documentRejectionError");
						break;
				}

				openNotification(
					t("documents.documentRejection"),
					notificationMessage,
					NOTIFICATION_TYPES.ERROR
				);
			})
			.finally(() => {
				setSpinning(false);
			});
	};

	const columns = [
		{
			title: t("documents.documentType"),
			dataIndex: "documentType",
			key: "documentType",
			fixed: "left" as "left",
			width: 110,
			render: (text: string | undefined, record: any) => {
				return <span>{t("documents." + record.documentType)}</span>;
			},
		},
		{
			title: !isCompany
				? t("documents.faculty")
				: t("documents.signedWithFaculty"),
			dataIndex: "facultyNameRo",
			key: "facultyNameRo",
			width: 200,
			responsive: ["md"] as unknown as Breakpoint[],
		},
		{
			title: t("documents.uploadDate"),
			dataIndex: "generationDate",
			key: "generationDate",
			width: 100,
			render: (text: string | undefined, record: any) => {
				return <span>{dayjs(record.generationDate).format("DD.MM.YYYY")}</span>;
			},
			ellipsis: true,
			responsive: ["lg"] as unknown as Breakpoint[],
		},
		{
			title: t("documents.expirationDate"),
			dataIndex: "expirationDate",
			key: "expirationDate",
			width: 130,
			render: (text: string | undefined, record: any) => {
				if (!record.isExpirationApplicable) {
					return <span>{t("documents.notApplicable")}</span>;
				}
				return (
					<span>
						{record.expirationDate == null
							? t("documents.expirationDateNotSet")
							: dayjs(record.expirationDate).format("DD.MM.YYYY") +
							  " - " +
							  (dayjs(record.expirationDate).isBefore(dayjs())
									? t("documents.expiredStatus")
									: t("documents.activeStatus"))}
					</span>
				);
			},
			ellipsis: true,
			responsive: ["lg"] as unknown as Breakpoint[],
		},
		{
			title: t("documents.status"),
			dataIndex: "documentStatus",
			key: "documentStatus",
			width: 100,
			render: (text: string | undefined, record: any) => {
				return <span>{t("documents." + record.documentStatus)}</span>;
			},
			ellipsis: true,
			responsive: ["lg"] as unknown as Breakpoint[],
		},
		{
			title: t("documents.canPublish"),
			dataIndex: "skipOfferValidation",
			key: "skipOfferValidation",
			width: 130,
			render: (text: string | undefined, record: any) => {
				return getUserId().toString() === record.documentOwner ||
					(!isCompany && record.facultyId == adminData?.faculty?.id) ? (
					<Checkbox
						checked={record.skipOfferValidation}
						disabled={
							getUserId().toString() !== record.documentOwner &&
							!(!isCompany && record.facultyId == adminData?.faculty?.id)
						}
						onChange={(event: any) => onCheckboxChange(event, record.id)}
					/>
				) : (
					<span>
						{record.skipOfferValidation
							? t("documents.yes")
							: t("documents.no")}
					</span>
				);
			},
			hidden: isCompany,
			ellipsis: true,
			responsive: ["lg"] as unknown as Breakpoint[],
		},
		{
			title: t("documents.actions"),
			dataIndex: "actions",
			key: "actions",
			fixed: "right" as "right",
			width: 125,
			render: (text: string | undefined, record: any) => (
				<div className={styles.modalContainer} key={"actions-" + record.id}>
					<Space>
						<div className={styles.modalBtnContainer}>
							<Button
								style={{
									background: "transparent",
									border: "none",
									outline: "none",
									color: theme.black,
									boxShadow: "none",
									padding: 0,
									marginRight: "2px",
								}}
								title={t("documents.download")}
								onClick={() =>
									downloadFile(
										downloadDocument(record.id!),
										t("documents." + record.documentType) +
											"_" +
											record.facultyNameRo +
											(isAdmin || isFacultyAdmin
												? "_" + location.state?.company
												: "")
									).catch(downloadFail)
								}
								icon={
									<FontAwesomeIcon
										icon={solid("download")}
										style={{ paddingRight: "5%" }}
									/>
								}
							>
								{isCompany ? t("documents.downloadDocument") : ""}
							</Button>
						</div>
						{(getUserId().toString() === record.documentOwner &&
							isCompany &&
							record.documentStatus !== CompanyDocumentStatusEnum.Finalized) ||
						(!isCompany && record.facultyId == adminData?.faculty?.id) ? (
							<div className={styles.modalBtnContainer}>
								<Button
									style={{
										background: "transparent",
										border: "none",
										outline: "none",
										color: theme.black,
										boxShadow: "none",
										padding: 0,
										marginTop: 0,
									}}
									onClick={() => {
										setSelectedDocumentId(record.id);
										setSelectedFaculty(record.facultyId);
										setDocumentType("practiceProtocol");
										setIsModalVisible(true);
									}}
									title={t("documents.uploadDocumentTooltip")}
									icon={
										<FontAwesomeIcon
											icon={solid("upload")}
											style={{ paddingRight: "5%" }}
										/>
									}
								>
									{isCompany ? t("documents.uploadNewDocument") : ""}
								</Button>
							</div>
						) : null}
						{!isCompany &&
						record.documentStatus === CompanyDocumentStatusEnum.InProgress &&
						record.facultyId === adminData?.faculty?.id ? (
							<div className={styles.modalBtnContainer}>
								<Button
									style={{
										background: "transparent",
										border: "none",
										outline: "none",
										color: theme.black,
										boxShadow: "none",
										padding: 0,
										marginTop: 0,
									}}
									onClick={() => {
										setSelectedDocumentId(record.id);
										setIsRejectModalVisible(true);
									}}
									icon={
										<FontAwesomeIcon
											icon={solid("ban")}
											style={{ paddingRight: "5%" }}
										/>
									}
									title={t("documents.rejectDocument")}
								/>
							</div>
						) : null}
						{(isAdmin ||
							(isFacultyAdmin &&
								record.facultyId === adminData?.faculty?.id)) &&
						!isCompany ? (
							<div className={styles.modalBtnContainer}>
								<Button
									style={{
										background: "transparent",
										border: "none",
										outline: "none",
										color: theme.black,
										boxShadow: "none",
										padding: 0,
										marginTop: 0,
									}}
									onClick={() => {
										setSelectedDocumentId(record.id);
										setModalVisibility(true);
									}}
									icon={
										<FontAwesomeIcon
											icon={solid("trash")}
											style={{ paddingRight: "5%" }}
										/>
									}
									title={t("documents.delete")}
								/>
								<ConfirmationModal
									modalText={t("documents.deleteWarning")}
									handleFunction={() => {
										deleteCompanyDocument(selectedDocumentId);
									}}
									modalVisibility={modalVisibility}
									title=""
									changeModalVisibility={() => setModalVisibility(false)}
									spinning={spinning}
								/>
							</div>
						) : null}
						{(isAdmin ||
							(isFacultyAdmin &&
								record.facultyId === adminData?.faculty?.id)) &&
						!isCompany ? (
							<div className={styles.modalBtnContainer}>
								<Tooltip
									title={
										record.isExpirationApplicable
											? t("documents.updateExpiration")
											: t("documents.expirationDateNotAllowed")
									}
								>
									<span style={{ display: "inline-block" }}>
										<Button
											style={{
												background: "transparent",
												border: "none",
												color: theme.black,
												boxShadow: "none",
												padding: 0,
												opacity: record.isExpirationApplicable ? 1 : 0.5,
											}}
											disabled={!record.isExpirationApplicable}
											onClick={() => {
												if (record.isExpirationApplicable) {
													setUpdateDoc(record);
													setNewDate(
														record.expirationDate
															? dayjs(record.expirationDate)
															: null
													);
												}
											}}
											icon={
												<FontAwesomeIcon
													icon={solid("file-pen")}
													style={{ paddingRight: "5%" }}
												/>
											}
										/>
									</span>
								</Tooltip>
								<Modal
									open={!!updateDoc}
									title={t("documents.updateExpiration")}
									onCancel={() => setUpdateDoc(null)}
									onOk={async () => {
										try {
											await updateDocumentExpiration(
												updateDoc.id,
												newDate?.toDate?.() ?? newDate
											);
											openNotification(
												t("documents.updateExpiration"),
												t("documents.updateSuccess"),
												NOTIFICATION_TYPES.SUCCESS
											);
											setUpdateDoc(null);
											queryClient.invalidateQueries(getDocuments);
										} catch {
											openNotification(
												t("documents.updateExpiration"),
												t("documents.updateFailed"),
												NOTIFICATION_TYPES.ERROR
											);
										}
									}}
								>
									<DatePicker
										style={{ width: "100%" }}
										value={newDate}
										onChange={(d) => setNewDate(d)}
									/>
								</Modal>
							</div>
						) : null}
					</Space>
				</div>
			),
		},
	].filter((item) => !item.hidden);

	const openNotificationErrorFetchDocuments = (_ex: any) => {
		openNotification(
			t("usersText.errorTexts.error"),
			t("documents.documentsFetchFailed"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const openGetErrorNotification = (_error: any) => {
		openNotification(
			t("internships.error"),
			t("internships.fetchDataError"),
			NOTIFICATION_TYPES.ERROR
		);
	};

	const { data: allFacultiesList } = useQuery(
		[getFaculties],
		() => getAllFaculties("", 1, 20),
		{
			onError: (err) => {
				openGetErrorNotification(err);
			},
			refetchOnWindowFocus: false,
		}
	);

	const { data, isLoading: loading } = useQuery(
		[
			getDocuments,
			location.state?.companyId,
			documentTypes,
			currentPage,
			pageSize,
			searchTerm,
			isFacultyAdmin,
			isAdmin,
			isCompany,
			companyID,
			adminData,
			allFacultiesList,
			validity,
		],
		() => {
			if (isCompany) {
				return getCompanyDocuments(
					companyID,
					documentTypes,
					currentPage,
					pageSize,
					validity
				);
			} else if (location.state?.companyId) {
				return getCompanyDocuments(
					location.state.companyId,
					documentTypes,
					currentPage,
					pageSize,
					validity
				);
			}
		},
		{
			onError: openNotificationErrorFetchDocuments,
			onSuccess: (data) => {
				if (data) {
					if (data?.data !== null && data?.data !== undefined) {
						if (isCompany) {
							setDocumentsOptions([
								{
									label: t("documents.practiceProtocol"),
									value: DocumentTypeEnum.PracticeProtocol,
								},
							]);
							let auxFaculties = allFacultiesList;

							for (let i = 0; i < data?.data.length; i++) {
								let facultyId = data.data[i].facultyId;
								if (
									!(
										data.data[i].expirationDate &&
										dayjs(data.data[i].expirationDate).isBefore(dayjs())
									)
								) {
									auxFaculties = auxFaculties?.filter(
										(e) => e.id !== facultyId
									);
								}
							}
							setAllFacultyOptions(auxFaculties);
							setFacultyOptions(
								auxFaculties
									?.filter(
										(faculty: FacultyDTO) => faculty.facultyNameRo !== "Altele"
									)
									.map((faculty: FacultyDTO) => (
										<Option key={faculty.id} value={faculty.id}>
											{i18n.language === "en"
												? faculty.facultyNameEn?.replace("Faculty of", "")
												: faculty.facultyNameRo?.replace("Facultatea de", "")}
										</Option>
									))
							);
						} else {
							if (adminData?.faculty?.id) {
								setAdminFaculties([adminData.faculty.id]);
							}
							const hasProtocolForUserFaculty = data?.data.some(
								(e) =>
									e.documentType === DocumentTypeEnum.PracticeProtocol &&
									e.facultyId === adminData?.faculty?.id &&
									!(
										e.expirationDate &&
										dayjs(e.expirationDate).isBefore(dayjs())
									)
							);
							if (hasProtocolForUserFaculty) {
								setDocumentsOptions([
									{
										label: t("documents.collaborationAccord"),
										value: DocumentTypeEnum.CollaborationAccord,
									},
								]);
							} else {
								setDocumentsOptions([
									{
										label: t("documents.practiceProtocol"),
										value: DocumentTypeEnum.PracticeProtocol,
									},
									{
										label: t("documents.collaborationAccord"),
										value: DocumentTypeEnum.CollaborationAccord,
									},
								]);
							}
						}
					}
				}
			},
		}
	);

	useEffect(() => {
		setFacultyOptions(
			allFacultyOptions
				?.filter((faculty: FacultyDTO) => {
					return (
						facultySearch === "" ||
						faculty.facultyNameRo
							?.toLowerCase()
							.includes(facultySearch.toLowerCase()) ||
						faculty.facultyNameEn
							?.toLowerCase()
							.includes(facultySearch.toLowerCase())
					);
				})
				.filter((faculty: FacultyDTO) => faculty.facultyNameRo !== "Altele")
				.map((faculty: FacultyDTO) => (
					<Option key={faculty.id} value={faculty.id}>
						{i18n.language === "en"
							? faculty.facultyNameEn?.replace("Faculty of", "")
							: faculty.facultyNameRo?.replace("Facultatea de", "")}
					</Option>
				))
		);
	}, [facultySearch, allFacultiesList, allFacultyOptions, i18n.language]);

	const onChange = (event: string) => {
		setDocumentType(event);
	};

	const handleSelectedFaculties = (selected: any) => {
		setSelectedFaculty(selected);
	};

	const handleListChange = (current: number, pageSize: number) => {
		setCurrent(() => current);
		setPageSize(() => pageSize);
	};

	return (
		<>
			<div className="flex items-center flex-col sm:flex-row pb-5">
				<div className="flex items-center">
					{(isAdmin || isFacultyAdmin) && (
						<ArrowDownOutlined
							onClick={() => history.goBack()}
							className="rotate-90 text-4xl text-primary"
						/>
					)}
					{!isCompany && (
						<h2 className="px-4">
							{t("documents.documents") +
								(location.state?.company
									? " - " + location.state?.company
									: "")}
						</h2>
					)}
				</div>
				{isCompany && <GenerateUniversityAccordsModal />}
				<div className="flex flex-col px-5 gap-5">
					<div className="flex items-center w-full my-6">
						<CustomButton
							style={{ marginTop: 0 }}
							onClick={handleShow}
							disabled={
								!((!isCompany && adminFaculties?.length !== 0) || isCompany)
							}
						>
							{t("documents.uploadDocument")}
						</CustomButton>

						<Select
							style={{ width: 160, marginLeft: 12 }}
							value={validity}
							defaultValue={DocumentStatusEnum.All}
							onChange={(v) => {
								setValidity(v);
								setCurrent(1);
							}}
							options={[
								{
									label: t("documents.active"),
									value: DocumentStatusEnum.Active,
								},
								{
									label: t("documents.expired"),
									value: DocumentStatusEnum.Expired,
								},
								{
									label: t("documents.all"),
									value: DocumentStatusEnum.All,
								},
							]}
						/>
						<Modal
							open={isModalVisible}
							maskClosable={!spinning}
							onCancel={handleCancel}
							onOk={handleOk}
							title={""}
							min-width={360}
							width={window.innerWidth > 1215 ? "30%" : "50%"}
							footer={
								<CustomModalFooter
									handleClose={handleCancel}
									spinning={spinning}
									handleSave={handleOk}
									confirmButtonName={t("account.confirm")}
								/>
							}
						>
							<Spin spinning={spinning}>
								<p>{t("documents.selectDocumentText")}</p>
								<Row>
									{selectedDocumentId === undefined && (
										<Col span={24} style={{ marginBottom: "1em" }}>
											<Select
												value={documentType}
												onChange={onChange}
												placeholder={t("documents.selectDocumentType")}
												options={documentsOptions}
												allowClear
												style={{ width: "20rem" }}
											/>
										</Col>
									)}
									{!isCompany &&
										(documentType === DocumentTypeEnum.PracticeProtocol ||
											documentType === "practiceProtocol") && (
											<>
												<br />
												<DatePicker
													style={{ width: "20rem", marginBottom: "1em" }}
													value={expiresAt}
													onChange={(d) => setExpiresAt(d)}
													placeholder={t("documents.selectExpirationDate")}
												/>
											</>
										)}
									{isCompany && selectedDocumentId === undefined && (
										<Col span={24}>
											<Typography style={{ fontWeight: "bold", color: "red" }}>
												{t("documents.selectFacultyWarning")}
											</Typography>
											<Select
												value={selectedFaculty}
												allowClear
												filterOption={false}
												onSearch={setFacultySearch}
												showSearch
												placeholder={t("documents.selectFaculty")}
												style={{ width: "20rem" }}
												onChange={handleSelectedFaculties}
											>
												{facultyOptions}
											</Select>
										</Col>
									)}
									<Col span={24}>
										{isCompany && (
											<Typography
												style={{
													fontWeight: "bold",
													color: "red",
													marginTop: "2rem",
												}}
											>
												{t("documents.uploadPracticeProtocolWarning")}
											</Typography>
										)}
										<Upload
											accept=".pdf"
											fileList={fileList}
											action={(uploaded) => {
												setFile(uploaded);
												return returnAction;
											}}
											onChange={(info) => setFileList(info.fileList)}
											showUploadList={true}
											multiple={false}
											customRequest={({ onSuccess }) => {
												onSuccess && onSuccess("");
											}}
										>
											<Button
												disabled={
													(!isCompany && documentType === null) ||
													(isCompany &&
														(documentType === null ||
															selectedFaculty === undefined))
												}
												icon={<FontAwesomeIcon icon={solid("upload")} />}
												title={t("documents.uploadPdf")}
											>
												&nbsp;{t("documents.upload")}
											</Button>
										</Upload>
									</Col>
								</Row>
							</Spin>
						</Modal>
						<Modal
							open={isRejectModalVisible}
							onCancel={handleCancel}
							onOk={() =>
								rejectCompanyDocument(selectedDocumentId, rejectionMessage)
							}
							title={t("documents.documentRejection")}
							width={window.innerWidth > 1215 ? "50%" : "80%"}
							footer={
								<CustomModalFooter
									handleClose={handleCancel}
									spinning={spinning}
									handleSave={() =>
										rejectCompanyDocument(selectedDocumentId, rejectionMessage)
									}
									confirmButtonName={t("account.confirm")}
								/>
							}
						>
							<Spin spinning={spinning}>
								<p>{t("documents.rejectDocumentMessage")}</p>

								<CustomEditor
									content={rejectionMessage}
									onEditorChange={(newValue: string) =>
										setRejectionMessage(newValue)
									}
								/>
							</Spin>
						</Modal>
					</div>
				</div>
			</div>

			<div className="flex flex-col">
				<div className="flex flex-col gap-10">
					<div className="flex flex-col md:flex-row justify-between items-center">
						<Typography.Title level={3} className="my-0">
							{data?.totalCount} {t("filtering.results")}
						</Typography.Title>
						{!loading && (
							<Pagination
								current={currentPage}
								pageSize={pageSize}
								total={data?.totalCount}
								onChange={handleListChange}
								locale={{ items_per_page: t("pagination") }}
							/>
						)}
					</div>

					<Table
						locale={locale}
						columns={columns}
						dataSource={data?.data ?? []}
						pagination={false}
						scroll={{ x: "calc(700px + 50%)", y: 420 }}
						rowKey={(record) => record.id!}
					/>
				</div>
			</div>
		</>
	);
};

export default DocumentsTable;
